/**
 * Based on previous ApplicationForm in Favourites list
 * TODO: Should be analysed and rewritten after "global state management decision"
 */

import {useEffect, useRef} from "react";
import {connect, useSelector} from "react-redux";
import {useFormikContext} from "formik";
import {bindActionCreators, Dispatch} from "redux";

import {omit, pick} from "@pg-mono/nodash";
import {IAppError} from "@pg-mono/request-state";

import {InquiryButtonTypeName} from "../../../../constants/inquiry_button_type";
import {IRPStore} from "../../../app/rp_reducer";
import {gtmInquiryOnApplicationSuccess} from "../../../application/tracking/lead/gtm_inquiry_on_application_success";
import {IApplicationReducerData} from "../../../application/types/IApplicationReducerData";
import {GTMEventApplicationSourceSection} from "../../../application/types/lead/GTMEvenApplicationSourceSection";
import {IPostApplicationPayload} from "../../../application/types/lead/IPostApplicationPayload";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {getSentLeadFormValuesFromStorage, setSentLeadFormValuesInStorage} from "../../../application/utils/lead/sent_lead_form_values";
import {authSucceeded} from "../../../auth/actions/auth_succeeded_event";
import {AuthActionType} from "../../../auth/types/AuthActionType";
import {fromJsonValues} from "../../../form/translate/from_json_values";
import {toJsonValues} from "../../../form/translate/to_json_values";
import {getPhoneString} from "../../../form/utils/get_phone_string";
import {useMyOffersFavourites} from "../../../my_offers/hooks/use_my_offers_favourites";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {OfferDisplayType} from "../../../offer/helpers/OfferDisplayType";
import {addAppliedOffers} from "../../../offer/map/actions/add_applied_offers";
import {hitGTMEcommercePurchase} from "../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_purchase";
import {IOfferDetail} from "../../../offer/types/IOfferDetail";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {isPropertyArchived} from "../../../property/utils/is_property_archived";
import {IAppDispatch} from "../../../store/store";
import {BoundAction} from "../../../store/utils/bound_action";
import {hitApplicationFavouriteOffersError, hitApplicationFavouritesSuccess} from "../../../tracking/algolytics_hits/application_hit";
import {gtmEventApplicationRegister} from "../../../tracking/google_tag_manager/gtm_event_application_register";
import {gtmEventFormInquiry} from "../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {patchUserProfileFromApplication} from "../../../user/actions/patch_user_profile_from_application";
import {useGetUserProfileQuery, useLazyGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {ViewType} from "../../../view_type/ViewType";
import {postFavouritesApplication} from "../../actions/post_favourites_application";
import {isVendorExcluded} from "../../utils/is_vendor_excluded";
import {IFavouritesListApplicationFormValues} from "./IFavouritesListApplicationFormValues";
import {favouritesListApplicationFormFields} from "./utils";

type IProps = IStateProps & IActionsProps & IFavouritesApplicationFormHandlerProps;

export interface IFavouritesApplicationFormHandlerProps {
    onSubmitStart?: () => void;
    onSubmitSuccess?: (
        applicationData: IFavouriteData[],
        sentOffers: IOfferDetail[],
        rejectedOffers: IOfferDetail[],
        sentProperties: IPropertyDetail[],
        rejectedProperties: IPropertyDetail[]
    ) => void;
    onSubmitFinish?: () => void;
}

interface IActionsProps {
    postFavouritesApplication: BoundAction<typeof postFavouritesApplication>;
    gtmInquiryOnApplicationSuccess: typeof gtmInquiryOnApplicationSuccess;
    addAppliedOffers: typeof addAppliedOffers;
    patchUserProfileFromApplication: BoundAction<typeof patchUserProfileFromApplication>;
    dispatch: IAppDispatch;
}

interface IStateProps {}

export const FavouritesApplicationFormHandlerC = (props: IProps) => {
    const {onSubmitStart, onSubmitFinish, onSubmitSuccess} = props;

    const {favouriteOffers, favouriteProperties} = useMyOffersFavourites();
    const {values, setValues, isSubmitting, setSubmitting, setErrors} = useFormikContext<IFavouritesListApplicationFormValues>();

    const isAuthenticated = useIsAuthenticated();
    const {data: userProfileData} = useGetUserProfileQuery();
    const [fetchUserProfile] = useLazyGetUserProfileQuery();
    const viewTypeState = useSelector((state: IRPStore) => state.viewType);

    const userProfileDataUpdated = useRef<boolean>(false);

    const sendFavouritesApplication = (favouriteData: IFavouriteData) => {
        const source = favouriteData.type === "offer" ? ApplicationSource.FavouritesOfferInquiry : ApplicationSource.FavouritesPropertyInquiry;

        const applicationData: IPostApplicationPayload = {
            source,
            source_section: ApplicationSourceSection.FAVOURITES,
            text: "Jestem zainteresowany/a zakupem nieruchomości w Państwa inwestycji. Proszę o kontakt.",
            home_match_trigger: false,
            [favouriteData.type === "offer" ? "offer" : "property"]: favouriteData.id,
            ...(toJsonValues(favouritesListApplicationFormFields, values) as Pick<
                IPostApplicationPayload,
                "name" | "email" | "phone" | "marketing_email" | "marketing_sms"
            >)
        };

        return props.postFavouritesApplication(applicationData, {
            on400Response: (error) => {
                hitApplicationFavouriteOffersError(source, ApplicationSourceSection.FAVOURITES, {
                    appError: error.appError as IAppError,
                    extraPayloadViewType: ViewType.MY_FAVOURITES_LIST
                });

                if (error.appError) {
                    setErrors(error.appError.fieldErrors);

                    if (error.appError.nonFieldErrors.length) {
                        props.dispatch(addWarningNotification(error.appError.nonFieldErrors.join(" ")));
                    }
                }
            }
        });
    };

    useEffect(() => {
        (async function updateInitialFormValues() {
            //  TODO: Consider doing this before rendering the form
            const storageUserData = getSentLeadFormValuesFromStorage();

            if (isAuthenticated) {
                const result = await fetchUserProfile();

                if (result.data) {
                    const userProfileData = result.data;
                    const formCheckboxesToBeRestored = {
                        ...pick(storageUserData, ["marketing_email", "marketing_sms"])
                    };

                    const profileDataFormValues = fromJsonValues(favouritesListApplicationFormFields, {
                        ...values,
                        ...userProfileData,
                        ...formCheckboxesToBeRestored
                    });

                    setValues(profileDataFormValues as IFavouritesListApplicationFormValues);
                }
            }

            if (!isAuthenticated && storageUserData) {
                const formFieldsToRestore = ["email", "name", "phone", "marketing_email", "marketing_sms"] as (keyof typeof storageUserData)[];
                const restoredFormData = pick(storageUserData, formFieldsToRestore);

                const storageFormValues = fromJsonValues(favouritesListApplicationFormFields, {
                    ...values,
                    ...restoredFormData
                });

                setValues(storageFormValues as IFavouritesListApplicationFormValues);
            }
        })();
    }, []);

    useEffect(() => {
        (async function handleSubmit() {
            if (isSubmitting) {
                if (onSubmitStart) {
                    onSubmitStart();
                }

                const applicationProperties = getApplicationProperties(favouriteProperties);
                const applicationOffers = getApplicationOffers(favouriteOffers);
                const applicationData: IFavouriteData[] = [...applicationProperties.data, ...applicationOffers.data];

                //  Finally send application data
                try {
                    await asyncForEach(applicationData, async (element: IFavouriteData) => {
                        const application = await sendFavouritesApplication(element);

                        if (!application) {
                            await Promise.reject();
                        } else {
                            // user was created when submitting the application - try to log in
                            if (application.user_created) {
                                gtmEventApplicationRegister();
                                props.dispatch(
                                    authSucceeded({
                                        type: AuthActionType.REGISTRATION,
                                        userSource: UserSource.APPLICATION,
                                        registrationMethod: "email"
                                    })
                                );
                            }

                            // try to update logged-in user data
                            if (isAuthenticated && userProfileData && !application.user_created && !userProfileDataUpdated.current) {
                                await props.patchUserProfileFromApplication(userProfileData, application);

                                userProfileDataUpdated.current = true;
                            }

                            isAuthenticated && addAppliedOffers([application.offer]);

                            hitApplicationFavouritesSuccess(application, application.source_section, {extraPayloadViewType: ViewType.MY_FAVOURITES_LIST});

                            if (element.type === "offer") {
                                const currentOffer = favouriteOffers.filter((offer) => offer.id === element.id)[0];
                                const data = {
                                    offer: {detail: currentOffer},
                                    vendor: {detail: currentOffer.vendor}
                                } as unknown as IApplicationReducerData;

                                gtmInquiryOnApplicationSuccess(application, data, ViewType.MY_FAVOURITES_LIST, "standard");
                                hitGTMEcommercePurchase(data, ViewType.MY_FAVOURITES_LIST, "standard", ApplicationSourceSection.FAVOURITES, application);
                            } else {
                                const currentProperty = favouriteProperties.filter((property) => property.id === element.id)[0];

                                const data = {
                                    offer: {detail: currentProperty.offer},
                                    vendor: {detail: currentProperty.offer.vendor},
                                    property: {detail: omit(currentProperty, ["offer"])}
                                } as unknown as IApplicationReducerData;

                                gtmInquiryOnApplicationSuccess(application, data, ViewType.MY_FAVOURITES_LIST, "standard");
                                hitGTMEcommercePurchase(data, ViewType.MY_FAVOURITES_LIST, "standard", ApplicationSourceSection.FAVOURITES, application);
                                gtmEventFormInquiry(
                                    InquiryButtonTypeName.SUBMIT_APPLICATION,
                                    viewTypeState.current === ViewType.COMPARISON_TOOL
                                        ? GTMEventApplicationSourceSection.COMPARISON_TOOL
                                        : GTMEventApplicationSourceSection.SIDEBAR_MY_LIST,
                                    "",
                                    ""
                                );
                            }

                            await Promise.resolve();
                        }
                    });

                    //  After submit success
                    const userStorageDataToSave = pick(values, ["email", "name", "phone", "marketing_email", "marketing_sms"]);

                    setSentLeadFormValuesInStorage({
                        ...userStorageDataToSave,
                        phone: getPhoneString(userStorageDataToSave.phone)
                    });

                    if (onSubmitSuccess) {
                        onSubmitSuccess(
                            applicationData,
                            applicationOffers.sent,
                            applicationOffers.rejected,
                            applicationProperties.sent,
                            applicationProperties.rejected
                        );
                    }
                } catch (e: unknown) {
                    //  After submit failure
                    console.log("Error", e);
                }

                //  After success or failure
                if (onSubmitFinish) {
                    onSubmitFinish();
                }

                setSubmitting(false);
            }
        })();
    }, [isSubmitting]);

    return null;
};

export const FavouritesApplicationFormHandler = connect<IStateProps, IActionsProps, IFavouritesApplicationFormHandlerProps, IRPStore>(
    () => ({}),
    mapDispatchToProps
)(FavouritesApplicationFormHandlerC);

function mapDispatchToProps(dispatch: Dispatch): IActionsProps {
    return {
        dispatch,
        ...bindActionCreators(
            {
                postFavouritesApplication,
                addAppliedOffers,
                gtmInquiryOnApplicationSuccess,
                patchUserProfileFromApplication
            },
            dispatch
        )
    };
}

//  Utils
//Because of user creation and cookies each application should be send separately
async function asyncForEach(array: IFavouriteData[], callback: (element: IFavouriteData, index: number, array: IFavouriteData[]) => void) {
    for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
    }
}

/*
    Below functions provide filtered data for api requests and return information about filtering
*/
interface IApplicationItems<T> {
    data: IFavouriteData[];
    sent: T[];
    rejected: T[];
}

interface IFavouriteData {
    id: number;
    type: string;
}

const getApplicationProperties = (selectedProperties: IPropertyDetail[]) =>
    selectedProperties.reduce<IApplicationItems<IPropertyDetail>>(
        (acc, property) => {
            if (isOfferRejected(property.offer) || isPropertyArchived(property)) {
                return {
                    ...acc,
                    rejected: [...acc.rejected, property]
                };
            }

            return {
                ...acc,
                sent: [...acc.sent, property],
                data: [...acc.data, {type: "property", id: property.id}]
            };
        },
        {sent: [], rejected: [], data: []}
    );

const getApplicationOffers = (selectedOffers: IOfferDetail[]) =>
    selectedOffers.reduce<IApplicationItems<IOfferDetail>>(
        (acc, offer) => {
            if (isOfferRejected(offer)) {
                return {
                    ...acc,
                    rejected: [...acc.rejected, offer]
                };
            }

            return {
                ...acc,
                sent: [...acc.sent, offer],
                data: [...acc.data, {type: "offer", id: offer.id}]
            };
        },
        {sent: [], rejected: [], data: []}
    );

const isOfferRejected = (offer: IPropertyDetailOffer | IOfferDetail) =>
    isVendorExcluded(offer.vendor.id) || !offer.is_active || offer.configuration.display_type !== OfferDisplayType.FOR_SALE;
/**
 * Based on previous ApplicationForm in Favourites list
 * TODO: Should be analysed and rewritten after "global state management decision"
 */

import {useEffect, useRef} from "react";
import {connect, useSelector} from "react-redux";
import {useFormikContext} from "formik";
import {bindActionCreators, Dispatch} from "redux";

import {omit, pick} from "@pg-mono/nodash";
import {IAppError} from "@pg-mono/request-state";

import {InquiryButtonTypeName} from "../../../../constants/inquiry_button_type";
import {IRPStore} from "../../../app/rp_reducer";
import {gtmInquiryOnApplicationSuccess} from "../../../application/tracking/lead/gtm_inquiry_on_application_success";
import {IApplicationReducerData} from "../../../application/types/IApplicationReducerData";
import {GTMEventApplicationSourceSection} from "../../../application/types/lead/GTMEvenApplicationSourceSection";
import {IPostApplicationPayload} from "../../../application/types/lead/IPostApplicationPayload";
import {ApplicationSource, ApplicationSourceSection} from "../../../application/utils/form/application_form_source";
import {getSentLeadFormValuesFromStorage, setSentLeadFormValuesInStorage} from "../../../application/utils/lead/sent_lead_form_values";
import {authSucceeded} from "../../../auth/actions/auth_succeeded_event";
import {AuthActionType} from "../../../auth/types/AuthActionType";
import {fromJsonValues} from "../../../form/translate/from_json_values";
import {toJsonValues} from "../../../form/translate/to_json_values";
import {getPhoneString} from "../../../form/utils/get_phone_string";
import {useMyOffersFavourites} from "../../../my_offers/hooks/use_my_offers_favourites";
import {addWarningNotification} from "../../../notifications/utils/add_warning_notification";
import {OfferDisplayType} from "../../../offer/helpers/OfferDisplayType";
import {addAppliedOffers} from "../../../offer/map/actions/add_applied_offers";
import {hitGTMEcommercePurchase} from "../../../offer/tracking/ecommerce_events/hit_gtm_ecommerce_purchase";
import {IOfferDetail} from "../../../offer/types/IOfferDetail";
import {IPropertyDetail} from "../../../property/types/IPropertyDetail";
import {IPropertyDetailOffer} from "../../../property/types/IPropertyDetailOffer";
import {isPropertyArchived} from "../../../property/utils/is_property_archived";
import {IAppDispatch} from "../../../store/store";
import {BoundAction} from "../../../store/utils/bound_action";
import {hitApplicationFavouriteOffersError, hitApplicationFavouritesSuccess} from "../../../tracking/algolytics_hits/application_hit";
import {gtmEventApplicationRegister} from "../../../tracking/google_tag_manager/gtm_event_application_register";
import {gtmEventFormInquiry} from "../../../tracking/google_tag_manager/gtm_event_form_inquiry";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {patchUserProfileFromApplication} from "../../../user/actions/patch_user_profile_from_application";
import {useGetUserProfileQuery, useLazyGetUserProfileQuery} from "../../../user/api/get_user_profile";
import {useIsAuthenticated} from "../../../user/hooks/use_is_authenticated";
import {ViewType} from "../../../view_type/ViewType";
import {postFavouritesApplication} from "../../actions/post_favourites_application";
import {isVendorExcluded} from "../../utils/is_vendor_excluded";
import {IFavouritesListApplicationFormValues} from "./IFavouritesListApplicationFormValues";
import {favouritesListApplicationFormFields} from "./utils";

type IProps = IStateProps & IActionsProps & IFavouritesApplicationFormHandlerProps;

export interface IFavouritesApplicationFormHandlerProps {
    onSubmitStart?: () => void;
    onSubmitSuccess?: (
        applicationData: IFavouriteData[],
        sentOffers: IOfferDetail[],
        rejectedOffers: IOfferDetail[],
        sentProperties: IPropertyDetail[],
        rejectedProperties: IPropertyDetail[]
    ) => void;
    onSubmitFinish?: () => void;
}

interface IActionsProps {
    postFavouritesApplication: BoundAction<typeof postFavouritesApplication>;
    gtmInquiryOnApplicationSuccess: typeof gtmInquiryOnApplicationSuccess;
    addAppliedOffers: typeof addAppliedOffers;
    patchUserProfileFromApplication: BoundAction<typeof patchUserProfileFromApplication>;
    dispatch: IAppDispatch;
}

interface IStateProps {}

export const FavouritesApplicationFormHandlerC = (props: IProps) => {
    const {onSubmitStart, onSubmitFinish, onSubmitSuccess} = props;

    const {favouriteOffers, favouriteProperties} = useMyOffersFavourites();
    const {values, setValues, isSubmitting, setSubmitting, setErrors} = useFormikContext<IFavouritesListApplicationFormValues>();

    const isAuthenticated = useIsAuthenticated();
    const {data: userProfileData} = useGetUserProfileQuery();
    const [fetchUserProfile] = useLazyGetUserProfileQuery();
    const viewTypeState = useSelector((state: IRPStore) => state.viewType);

    const userProfileDataUpdated = useRef<boolean>(false);

    const sendFavouritesApplication = (favouriteData: IFavouriteData) => {
        const source = favouriteData.type === "offer" ? ApplicationSource.FavouritesOfferInquiry : ApplicationSource.FavouritesPropertyInquiry;

        const applicationData: IPostApplicationPayload = {
            source,
            source_section: ApplicationSourceSection.FAVOURITES,
            text: "Jestem zainteresowany/a zakupem nieruchomości w Państwa inwestycji. Proszę o kontakt.",
            home_match_trigger: false,
            [favouriteData.type === "offer" ? "offer" : "property"]: favouriteData.id,
            ...(toJsonValues(favouritesListApplicationFormFields, values) as Pick<
                IPostApplicationPayload,
                "name" | "email" | "phone" | "marketing_email" | "marketing_sms"
            >)
        };

        return props.postFavouritesApplication(applicationData, {
            on400Response: (error) => {
                hitApplicationFavouriteOffersError(source, ApplicationSourceSection.FAVOURITES, {
                    appError: error.appError as IAppError,
                    extraPayloadViewType: ViewType.MY_FAVOURITES_LIST
                });

                if (error.appError) {
                    setErrors(error.appError.fieldErrors);

                    if (error.appError.nonFieldErrors.length) {
                        props.dispatch(addWarningNotification(error.appError.nonFieldErrors.join(" ")));
                    }
                }
            }
        });
    };

    useEffect(() => {
        (async function updateInitialFormValues() {
            //  TODO: Consider doing this before rendering the form
            const storageUserData = getSentLeadFormValuesFromStorage();

            if (isAuthenticated) {
                const result = await fetchUserProfile();

                if (result.data) {
                    const userProfileData = result.data;
                    const formCheckboxesToBeRestored = {
                        ...pick(storageUserData, ["marketing_email", "marketing_sms"])
                    };

                    const profileDataFormValues = fromJsonValues(favouritesListApplicationFormFields, {
                        ...values,
                        ...userProfileData,
                        ...formCheckboxesToBeRestored
                    });

                    setValues(profileDataFormValues as IFavouritesListApplicationFormValues);
                }
            }

            if (!isAuthenticated && storageUserData) {
                const formFieldsToRestore = ["email", "name", "phone", "marketing_email", "marketing_sms"] as (keyof typeof storageUserData)[];
                const restoredFormData = pick(storageUserData, formFieldsToRestore);

                const storageFormValues = fromJsonValues(favouritesListApplicationFormFields, {
                    ...values,
                    ...restoredFormData
                });

                setValues(storageFormValues as IFavouritesListApplicationFormValues);
            }
        })();
    }, []);

    useEffect(() => {
        (async function handleSubmit() {
            if (isSubmitting) {
                if (onSubmitStart) {
                    onSubmitStart();
                }

                const applicationProperties = getApplicationProperties(favouriteProperties);
                const applicationOffers = getApplicationOffers(favouriteOffers);
                const applicationData: IFavouriteData[] = [...applicationProperties.data, ...applicationOffers.data];

                //  Finally send application data
                try {
                    await asyncForEach(applicationData, async (element: IFavouriteData) => {
                        const application = await sendFavouritesApplication(element);

                        if (!application) {
                            await Promise.reject();
                        } else {
                            // user was created when submitting the application - try to log in
                            if (application.user_created) {
                                gtmEventApplicationRegister();
                                props.dispatch(
                                    authSucceeded({
                                        type: AuthActionType.REGISTRATION,
                                        userSource: UserSource.APPLICATION,
                                        registrationMethod: "email"
                                    })
                                );
                            }

                            // try to update logged-in user data
                            if (isAuthenticated && userProfileData && !application.user_created && !userProfileDataUpdated.current) {
                                await props.patchUserProfileFromApplication(userProfileData, application);

                                userProfileDataUpdated.current = true;
                            }

                            isAuthenticated && addAppliedOffers([application.offer]);

                            hitApplicationFavouritesSuccess(application, application.source_section, {extraPayloadViewType: ViewType.MY_FAVOURITES_LIST});

                            if (element.type === "offer") {
                                const currentOffer = favouriteOffers.filter((offer) => offer.id === element.id)[0];
                                const data = {
                                    offer: {detail: currentOffer},
                                    vendor: {detail: currentOffer.vendor}
                                } as unknown as IApplicationReducerData;

                                gtmInquiryOnApplicationSuccess(application, data, ViewType.MY_FAVOURITES_LIST, "standard");
                                hitGTMEcommercePurchase(data, ViewType.MY_FAVOURITES_LIST, "standard", ApplicationSourceSection.FAVOURITES, application);
                            } else {
                                const currentProperty = favouriteProperties.filter((property) => property.id === element.id)[0];

                                const data = {
                                    offer: {detail: currentProperty.offer},
                                    vendor: {detail: currentProperty.offer.vendor},
                                    property: {detail: omit(currentProperty, ["offer"])}
                                } as unknown as IApplicationReducerData;

                                gtmInquiryOnApplicationSuccess(application, data, ViewType.MY_FAVOURITES_LIST, "standard");
                                hitGTMEcommercePurchase(data, ViewType.MY_FAVOURITES_LIST, "standard", ApplicationSourceSection.FAVOURITES, application);
                                gtmEventFormInquiry(
                                    InquiryButtonTypeName.SUBMIT_APPLICATION,
                                    viewTypeState.current === ViewType.COMPARISON_TOOL
                                        ? GTMEventApplicationSourceSection.COMPARISON_TOOL
                                        : GTMEventApplicationSourceSection.SIDEBAR_MY_LIST,
                                    "",
                                    ""
                                );
                            }

                            await Promise.resolve();
                        }
                    });

                    //  After submit success
                    const userStorageDataToSave = pick(values, ["email", "name", "phone", "marketing_email", "marketing_sms"]);

                    setSentLeadFormValuesInStorage({
                        ...userStorageDataToSave,
                        phone: getPhoneString(userStorageDataToSave.phone)
                    });

                    if (onSubmitSuccess) {
                        onSubmitSuccess(
                            applicationData,
                            applicationOffers.sent,
                            applicationOffers.rejected,
                            applicationProperties.sent,
                            applicationProperties.rejected
                        );
                    }
                } catch (e: unknown) {
                    //  After submit failure
                    console.log("Error", e);
                }

                //  After success or failure
                if (onSubmitFinish) {
                    onSubmitFinish();
                }

                setSubmitting(false);
            }
        })();
    }, [isSubmitting]);

    return null;
};

export const FavouritesApplicationFormHandler = connect<IStateProps, IActionsProps, IFavouritesApplicationFormHandlerProps, IRPStore>(
    () => ({}),
    mapDispatchToProps
)(FavouritesApplicationFormHandlerC);

function mapDispatchToProps(dispatch: Dispatch): IActionsProps {
    return {
        dispatch,
        ...bindActionCreators(
            {
                postFavouritesApplication,
                addAppliedOffers,
                gtmInquiryOnApplicationSuccess,
                patchUserProfileFromApplication
            },
            dispatch
        )
    };
}

//  Utils
//Because of user creation and cookies each application should be send separately
async function asyncForEach(array: IFavouriteData[], callback: (element: IFavouriteData, index: number, array: IFavouriteData[]) => void) {
    for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
    }
}

/*
    Below functions provide filtered data for api requests and return information about filtering
*/
interface IApplicationItems<T> {
    data: IFavouriteData[];
    sent: T[];
    rejected: T[];
}

interface IFavouriteData {
    id: number;
    type: string;
}

const getApplicationProperties = (selectedProperties: IPropertyDetail[]) =>
    selectedProperties.reduce<IApplicationItems<IPropertyDetail>>(
        (acc, property) => {
            if (isOfferRejected(property.offer) || isPropertyArchived(property)) {
                return {
                    ...acc,
                    rejected: [...acc.rejected, property]
                };
            }

            return {
                ...acc,
                sent: [...acc.sent, property],
                data: [...acc.data, {type: "property", id: property.id}]
            };
        },
        {sent: [], rejected: [], data: []}
    );

const getApplicationOffers = (selectedOffers: IOfferDetail[]) =>
    selectedOffers.reduce<IApplicationItems<IOfferDetail>>(
        (acc, offer) => {
            if (isOfferRejected(offer)) {
                return {
                    ...acc,
                    rejected: [...acc.rejected, offer]
                };
            }

            return {
                ...acc,
                sent: [...acc.sent, offer],
                data: [...acc.data, {type: "offer", id: offer.id}]
            };
        },
        {sent: [], rejected: [], data: []}
    );

const isOfferRejected = (offer: IPropertyDetailOffer | IOfferDetail) =>
    isVendorExcluded(offer.vendor.id) || !offer.is_active || offer.configuration.display_type !== OfferDisplayType.FOR_SALE;
